import { useEffect } from "react";
import {
  playOcean, stopOcean,
  playAmbientMusic, stopAmbientMusic, isAmbientPlaying,
} from "./chime";

const STORAGE_KEY = "luxstay-ambient-music";

/**
 * useContextualAudio — mounts a specific ambient soundscape per area, but ONLY
 * if the user has explicitly enabled ambient music via the MusicToggle (so we
 * never violate the autoplay policy and we honour the user's mute preference).
 *
 * Supported soundscapes:
 *   "ocean"   → low, wide ocean-waves texture (AdminDashboard)
 *   "ambient" → slow piano + warm pad loop (default; Invoices, list pages)
 *   "silence" → explicitly stop any audio
 *
 * Switching between areas smoothly crossfades: stop the previous, start the
 * new one only if ambient music is ON. Cleanup on unmount stops the layer
 * owned by this hook instance.
 */
export function useContextualAudio(kind) {
  useEffect(() => {
    const enabled = (() => {
      try { return localStorage.getItem(STORAGE_KEY) === "1"; }
      catch { return false; }
    })();

    if (!enabled || kind === "silence") {
      // If the user has music off, don't force anything. When they already
      // enabled ambient elsewhere, we still never auto-start ocean.
      return () => { /* nothing to tear down */ };
    }

    let mounted = true;
    if (kind === "ocean") {
      // Stop any previous lounge music, start ocean waves
      stopAmbientMusic({ fadeMs: 500 });
      setTimeout(() => {
        if (mounted) playOcean({ volume: 0.05 });
      }, 200);
      return () => {
        mounted = false;
        stopOcean({ fadeMs: 700 });
        // Resume default ambient if user's MusicToggle is still ON
        setTimeout(() => {
          try {
            if (localStorage.getItem(STORAGE_KEY) === "1" && !isAmbientPlaying()) {
              playAmbientMusic({ volume: 0.09 });
            }
          } catch { /* ignore */ }
        }, 800);
      };
    }

    if (kind === "ambient") {
      // Ensure ambient music is playing when entering a focus page
      if (!isAmbientPlaying()) {
        playAmbientMusic({ volume: 0.09 });
      }
      return () => { /* leave ambient running; other pages may need it */ };
    }

    return () => { /* unknown kind → no-op */ };
  }, [kind]);
}
